import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';

import { useIsCurrentUserAdmin } from 'src/store';
import { useRestrictActionPopup, useIsCreatingResource } from 'src/services';
import { formatFileSize } from 'src/utils/format';
import { EMAIL_CSV_RESOURCES, RESOURCE_DOCUMENT_PROPS, RESOURCE_TYPES } from 'src/data/performance';
import { Button } from 'src/components/Button';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { useStyles } from './useStyles';

export const AddFileResourcePopup = ({ open, onSubmit, onClose, type, allowReplace }) => {
  const styles = useStyles();

  const isCurrentUserAdmin = useIsCurrentUserAdmin();
  const openRestrictActionPopup = useRestrictActionPopup();
  const isCreatingResource = useIsCreatingResource();
  const fileInputRef = useRef();

  const [resource, setResource] = useState();
  const [isFileInputRefVisible, setIsFileInputRefVisible] = useState();

  const handleConfirm = useCallback(() => {
    if (!isCurrentUserAdmin) {
      openRestrictActionPopup();
      return;
    }
    onSubmit({
      resource,
    });
  }, [resource, isCurrentUserAdmin, openRestrictActionPopup, onSubmit]);

  const handleResourceClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleResourceChange = useCallback(
    (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];

      if (!selectedFile) {
        if (!allowReplace) {
          onClose();
        }
        return;
      }

      if (!allowReplace) {
        onSubmit({
          resource: selectedFile,
        });
      } else {
        setResource(selectedFile);
      }
    },
    [allowReplace, onSubmit, onClose]
  );

  useLayoutEffect(() => {
    if (isFileInputRefVisible) {
      fileInputRef.current?.click();
    }
  }, [fileInputRef, isFileInputRefVisible]);

  const { getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      ...(EMAIL_CSV_RESOURCES.includes(type) ? { 'text/csv': ['.csv'] } : {}),
    },
    onDrop: handleResourceChange,
    onFileDialogCancel: onClose,
    maxFiles: 1,
  });

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={isCreatingResource ? null : onClose}
      maxWidth="md"
      data-testid="AddFileResourcePopup"
    >
      <DialogTitle>
        <Typography className={styles.title}>
          Upload {RESOURCE_DOCUMENT_PROPS[type]?.label || 'file'}
        </Typography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <input
          {...getInputProps()}
          ref={(el) => {
            fileInputRef.current = el;
            setIsFileInputRefVisible(!!el);
          }}
          style={{ visibility: 'hidden' }}
        />
        <Typography className={styles.label}>Selected file</Typography>
        <div className={styles.urlWrapper}>
          <div className={styles.fileNameWrapper}>
            {resource ? (
              <>
                <TruncatedTypography className={styles.fileName}>
                  {resource?.name}
                </TruncatedTypography>
                <Typography className={styles.fileSize}>({formatFileSize(resource)})</Typography>
                <Typography
                  className={styles.replace}
                  onClick={handleResourceClick}
                >
                  Replace
                </Typography>
              </>
            ) : (
              <Typography
                className={styles.replace}
                onClick={handleResourceClick}
              >
                Upload
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isCreatingResource}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="AddFileResourcePopupCancelButton"
        >
          Cancel
        </Button>
        {allowReplace && (
          <Button
            onClick={handleConfirm}
            disabled={!resource || isCreatingResource}
            loading={isCreatingResource}
            variant="contained"
            color="primary"
            aria-label="Confirm"
            data-testid="AddFileResourcePopupCTAButton"
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AddFileResourcePopup.propTypes = {
  type: PropTypes.oneOf(RESOURCE_TYPES).isRequired,
  open: PropTypes.bool,
  allowReplace: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};
AddFileResourcePopup.defaultProps = {
  open: false,
  onClose: null,
  onSubmit: null,
};
